import { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { RewardHistoryFragment, useGetRewardsHistoryLazyQuery } from 'shared/types/graphql';

export const useGetRewardsHistory = (
  accountId?: string,
): {
  rewardsHistory: RewardHistoryFragment[];
  rewardsHistoryCount: number;
  fetchMore: () => Promise<void>;
  loading: boolean;
} => {
  const [take, setTake] = useState(10);

  const [
    getRewardsHistoryData,
    { data: rewardsHistoryData, fetchMore: fetchMoreRewardsHistoryEntries, loading: isRewardsHistoryDataLoading },
  ] = useGetRewardsHistoryLazyQuery({
    variables: {
      where: {
        accountId: {
          equals: accountId,
        },
        points: {
          not: {
            equals: 0,
          },
        },
        postedToAccount: {
          equals: true,
        },
      },
      take,
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });

  useEffect(() => {
    if (accountId) {
      getRewardsHistoryData();
    }
  }, [accountId, getRewardsHistoryData]);

  const rewardsHistory = useMemo((): RewardHistoryFragment[] => {
    return rewardsHistoryData?.findManyLoyaltyPointDelta || [];
  }, [rewardsHistoryData]);

  const rewardsHistoryCount = rewardsHistoryData?.findManyLoyaltyPointDeltaCount || 0;

  const loadMoreRewardsHistoryEntries = useCallback(() => {
    if (accountId && fetchMoreRewardsHistoryEntries) {
      setTake(take + rewardsHistory.length);
      fetchMoreRewardsHistoryEntries({
        variables: {
          take,
        },
      });
    }
  }, [accountId, fetchMoreRewardsHistoryEntries, rewardsHistory.length, take]);

  return {
    rewardsHistory,
    rewardsHistoryCount,
    fetchMore: async (): Promise<void> => {
      await loadMoreRewardsHistoryEntries();
    },
    loading: isRewardsHistoryDataLoading,
  };
};
